/**
 * The BD home's "Upcoming Events" cards: which events, in what order (Event Hub Testing doc #22).
 *
 * Two groups, in this order:
 *
 *   1. Open for nomination now, to this BD's BUs (isNominatable — the same predicate as the
 *      "Open for Nomination" count), soonest nomination deadline first: what a BD has to act on
 *      next. An open event for another BU is in neither group: there is nothing this BD can do.
 *   2. Published events that take no nominations, soonest start first: worth knowing about,
 *      nothing to do.
 *
 * Limited to the coming month: group 1 by nomination deadline, group 2 by start date. A
 * nomination-enabled event whose window is not open right now (not yet open, or closed) is in
 * neither group — there is nothing a BD can do with it today.
 *
 * Because of the one-month limit, an open event whose deadline is further out is counted by
 * "Open for Nomination" but not shown here; the count's link (the calendar table, filtered to
 * open events) lists every one.
 */
import type { Event } from '../client/EventCalendarClient';
import { isNominatable } from './nominatableEvents';

/** The end of the window: one calendar month after `now`. */
export function oneMonthAfter(now: Date): Date {
  const end = new Date(now);
  end.setMonth(end.getMonth() + 1);
  return end;
}

/** A published event that does not take nominations, starting between today and `until`. */
function isUpcomingWithoutNomination(event: Event, now: Date, until: Date): boolean {
  if (event.contentStatus !== 'PUBLISHED' || event.bdNominationSupported) return false;
  const start = event.startDate;
  if (!start) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return start >= today && start <= until;
}

const time = (d: Date | undefined) => d?.getTime() ?? 0;

export function upcomingEvents(
  events: readonly Event[],
  now: Date,
  bdBusinessUnits?: readonly string[],
): Event[] {
  const until = oneMonthAfter(now);
  const open = events
    .filter((e) => isNominatable(e, now, bdBusinessUnits) && e.nominationEndDate! <= until)
    .sort((a, b) => time(a.nominationEndDate) - time(b.nominationEndDate));
  const noNomination = events
    .filter((e) => isUpcomingWithoutNomination(e, now, until))
    .sort((a, b) => time(a.startDate) - time(b.startDate));
  return [...open, ...noNomination];
}
